import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import PageRenderer from '../../components/common/PageRenderer';

const heroSlides = [
  {
    image: '/AP001.jpeg',
    quote: 'Surveyors don’t guess — they confirm, interpret, and calculate with precision.',
    cta: { label: 'Scale of Fees', to: '/scaleoffees' },
    anim: 'animate-fadeInRight',
  },
  {
    image: '/AP002.jpeg',
    quote: 'Turning terrain into truth through data, analysis, and exact measurement.',
    cta: { label: 'Find a Surveyor', to: '/find-surveyor' },
    anim: 'animate-fadeIn',
  },
  {
    image: '/AP003.jpeg',
    quote: 'Before any foundation is laid, surveyors bring clarity through precise measurement and analysis.',
    cta: { label: 'About Us', to: '/about' },
    anim: 'animate-zoomIn',
  },
  {
    image: '/AP004.jpeg',
    quote: 'Every accurate measurement begins with a surveyor who turns uncertainty into defined reality.',
    cta: { label: 'Contact Us', to: '/contact' },
    anim: 'animate-slideUp',
  },
];

function AnnouncementsSection() {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    api.get('/announcements/').then(res => {
      setAnnouncements(res.data.results || res.data || []);
    }).catch(() => {});
  }, []);

  if (announcements.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] section-heading">
              Latest News & Announcements
            </h2>
          </div>
          <Link to="/about" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-[#0B5D1E] hover:text-[#1A7A35] link-underline transition">
            View all
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {announcements.slice(0, 3).map((a) => (
            <article key={a.id} className="card-hover bg-[#F8FAFB] rounded-xl overflow-hidden border border-gray-100">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider ${
                    a.priority === 'urgent' ? 'badge-urgent' :
                    a.priority === 'high' ? 'badge-high' : 'badge-normal'
                  }`}>
                    {a.priority}
                  </span>
                  <time className="text-xs text-gray-400 font-medium">
                    {new Date(a.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </time>
                </div>
                <h3 className="text-lg font-bold text-[#111827] mb-3 leading-snug line-clamp-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {a.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{a.content}</p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <span className="text-[#0B5D1E] text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all cursor-pointer">
                    Read more
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function QuickAccess() {
  const items = [
    {
      label: 'Scale of Fees',
      desc: 'Official surveying fee structure',
      to: '/scaleoffees',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
        </svg>
      ),
    },
    {
      label: 'Find a Surveyor',
      desc: 'Search certified FCT surveyors',
      to: '/find-surveyor',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
        </svg>
      ),
    },
    {
      label: 'About Us',
      desc: 'Our mission, vision and history',
      to: '/about',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      label: 'Contact Us',
      desc: 'Reach the APPSN secretariat',
      to: '/contact',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];
  return (
    <section id="quick-links" className="bg-[#0B5D1E] py-12 md:py-16 relative z-10">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8 md:mb-10">
          <div className="inline-block text-[#C8A951] text-xs font-bold tracking-[0.25em] uppercase mb-2">Explore</div>
          <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Quick Links
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {items.map((b) => (
            <Link
              key={b.to}
              to={b.to}
              className="group relative bg-white/95 backdrop-blur border border-white/20 rounded-xl p-5 hover:bg-white hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex items-start gap-4 overflow-hidden"
            >
              <span className="absolute inset-x-0 top-0 h-1 bg-[#C8A951] scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300" />
              <div className="shrink-0 w-12 h-12 rounded-lg bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center group-hover:bg-[#0B5D1E] group-hover:text-white transition-colors duration-300">
                {b.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-bold text-[#111827] group-hover:text-[#0B5D1E] transition-colors">
                  {b.label}
                </div>
                <div className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-2">
                  {b.desc}
                </div>
              </div>
              <svg className="w-4 h-4 text-gray-300 group-hover:text-[#0B5D1E] mt-1 group-hover:translate-x-1 transition-all duration-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryGrid() {
  return (
    <div className="max-w-7xl mx-auto px-4 pb-16">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {['/Surveyor001.jpeg', '/Surveyor002.jpeg', '/Surveyor003.jpeg', '/Surveyor004.jpeg'].map((src, i) => (
          <div key={i} className="relative group overflow-hidden rounded-xl aspect-[4/3]">
            <img src={src} alt={`Surveyor at work ${i + 1}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DefaultContent() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  // Auto-slide every 5 seconds
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <>
      {/* Full-width Image Slider */}
      <section className="relative">
        <div className="relative overflow-hidden bg-black/10" style={{ minHeight: '540px' }}>
              {/* Slides */}
              {heroSlides.map((slide, index) => (
                <div
                  key={slide.image}
                  className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                  style={{ opacity: index === currentSlide ? 1 : 0, zIndex: index === currentSlide ? 1 : 0 }}
                >
                  <img src={slide.image} alt={`APPSN Slide ${index + 1}`} className="w-full h-full object-cover object-center" />
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/65 to-black/45"></div>
                </div>
              ))}

              {/* Quote + slide buttons overlay — re-keyed so animation re-fires per slide */}
              <div
                key={currentSlide}
                className={`absolute inset-x-0 top-0 bottom-20 z-10 flex flex-col items-center justify-center px-6 md:px-12 text-white pointer-events-none ${heroSlides[currentSlide].anim}`}
              >
                <p
                  className="max-w-3xl mx-auto text-center text-xl sm:text-2xl md:text-4xl font-semibold leading-snug drop-shadow-lg"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {heroSlides[currentSlide].quote}
                </p>
                <div className="pointer-events-auto mt-8 md:mt-10">
                  <Link
                    to={heroSlides[currentSlide].cta.to}
                    className="inline-flex items-center gap-2 px-7 md:px-9 py-3 md:py-4 text-sm md:text-base font-bold tracking-wide rounded-lg bg-[#C8A951] text-[#062E10] hover:bg-white hover:text-[#0B5D1E] transition-all duration-300 shadow-lg hover:shadow-2xl hover:-translate-y-0.5"
                  >
                    {heroSlides[currentSlide].cta.label}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Slide navigation arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition"
                aria-label="Previous slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center transition"
                aria-label="Next slide"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Slide dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2">
                {heroSlides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      i === currentSlide ? 'bg-white scale-110' : 'bg-white/50 hover:bg-white/70'
                    }`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
        </div>

        {/* Join APPSN TODAY — Red circle CTA (overlapping bottom) */}
        <div className="absolute bottom-0 left-8 md:left-16 translate-y-1/2 z-10">
          <div className="w-44 h-44 md:w-52 md:h-52 bg-[#C0392B] rounded-full flex flex-col items-center justify-center shadow-xl text-white text-center p-4">
            <span className="text-xl md:text-2xl font-bold leading-tight">Join APPSN<br />TODAY</span>
            <Link
              to="/register"
              className="mt-3 inline-block border-2 border-white text-white text-xs md:text-sm font-semibold px-5 py-1.5 rounded hover:bg-white hover:text-[#C0392B] transition"
            >
              Join Now
            </Link>
          </div>
        </div>
      </section>

      {/* Spacer for the overlapping circle */}
      <div className="h-28 bg-white"></div>

      {/* What We Offer */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>What We Offer</h2>
          <p className="text-gray-500 max-w-2xl mx-auto mb-12">
            Dedicated to supporting surveyors and advancing the profession in the Federal Capital Territory
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🎓', title: 'Professional Development', desc: 'Workshops, seminars, and CPD programs to keep members at the forefront of surveying practice.' },
              { icon: '🤝', title: 'Networking', desc: 'Connect with fellow surveyors, mentors, and industry leaders across the FCT and Nigeria.' },
              { icon: '📋', title: 'Regulation & Standards', desc: 'Upholding ethical standards and ensuring quality service delivery in all surveying activities.' },
              { icon: '🏛️', title: 'Advocacy', desc: 'Representing members\' interests at government and institutional levels for a better profession.' },
            ].map((item) => (
              <div key={item.title} className="bg-[#F8FAFB] rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-bold text-[#0B5D1E] mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Announcements */}
      <AnnouncementsSection />

      {/* Gallery */}
      <section className="py-16 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display',serif" }}>Gallery</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Highlights from our events, workshops, and activities</p>
          </div>
          <GalleryGrid />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#f0ede8]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Join the APPSN FCT Branch Community</h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            Whether you're a licensed surveyor, graduate, or student, there's a place for you in our community.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-[#0B5D1E] text-white px-8 py-3.5 rounded text-sm font-bold hover:bg-[#094A18] transition shadow-lg">
            Get in Touch
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </Link>
        </div>
      </section>
    </>
  );
}

export default function Home() {
  return (
    <PageRenderer
      pageKey="home"
      insertAfter={{
        slider: () => <QuickAccess />,
        hero: () => <QuickAccess />,
        'Features Grid — What We Offer': () => <AnnouncementsSection />,
        'Gallery Description': () => <GalleryGrid />,
      }}
      fallback={<DefaultContent />}
    />
  );
}
